/**
 * Cache client-side con TTL.
 * Ora usa localStorage (persiste anche dopo chiusura del tab, non solo per sessione).
 * TTL default: 15 minuti — bilanciato tra freschezza e velocità.
 * Le mutazioni CRM invalidano automaticamente la cache backend, ma la cache client
 * può ancora servire dati leggermente vecchi tra utenti diversi. Al fetch avviene
 * comunque la revalidation in background.
 */

const DEFAULT_TTL_MS = 15 * 60 * 1000; // 15 minuti
const STORAGE = typeof window !== "undefined" ? window.localStorage : null;

export function readSessionCache(key, maxAgeMs = DEFAULT_TTL_MS) {
  try {
    if (!STORAGE) return null;
    const raw = STORAGE.getItem(key);
    if (!raw) return null;
    const entry = JSON.parse(raw);
    if (Date.now() - (entry.timestamp || 0) > maxAgeMs) return null;
    return entry.data;
  } catch {
    return null;
  }
}

/**
 * Legge la cache SENZA controllo TTL (stale-while-revalidate).
 * Utile per mostrare istantaneamente dati vecchi mentre in background si aggiorna.
 */
export function readStaleSessionCache(key) {
  try {
    if (!STORAGE) return null;
    const raw = STORAGE.getItem(key);
    if (!raw) return null;
    const entry = JSON.parse(raw);
    return entry.data;
  } catch {
    return null;
  }
}

export function writeSessionCache(key, data) {
  if (!STORAGE) return;
  const payload = JSON.stringify({ data, timestamp: Date.now() });
  try {
    STORAGE.setItem(key, payload);
  } catch (e) {
    // Quota superata: strategia di recovery.
    // 1. Rimuovi le entries piu' vecchie del prefisso dello stesso tipo (es. offers_*)
    // 2. Riprova a scrivere. Se ancora fallisce, cancella TUTTA la cache CRM e riprova.
    try {
      const prefix = key.split("_")[0];
      const candidates = Object.keys(STORAGE)
        .filter((k) => k.startsWith(prefix) && k !== key)
        .map((k) => {
          try {
            const entry = JSON.parse(STORAGE.getItem(k));
            return { k, timestamp: entry?.timestamp || 0 };
          } catch {
            return { k, timestamp: 0 };
          }
        })
        .sort((a, b) => a.timestamp - b.timestamp);
      for (const { k } of candidates) {
        STORAGE.removeItem(k);
        try {
          STORAGE.setItem(key, payload);
          return; // success
        } catch { /* continua a togliere */ }
      }
      // Ultima chance: pulisci TUTTA la cache CRM
      const crmPrefixes = ["dashboard_", "ordini_", "payments_", "interviews_", "commesse_pose_", "offers_"];
      for (const p of crmPrefixes) {
        for (const k of Object.keys(STORAGE)) {
          if (k.startsWith(p) && k !== key) STORAGE.removeItem(k);
        }
      }
      STORAGE.setItem(key, payload);
    } catch {
      // storage completamente indisponibile / dato oversized: silente
    }
  }
}

export function clearSessionCache(prefix) {
  try {
    if (!STORAGE) return;
    const keys = Object.keys(STORAGE);
    for (const k of keys) {
      if (!prefix || k.startsWith(prefix)) STORAGE.removeItem(k);
    }
  } catch { /* noop */ }
}
